/**
 * Towers Map Component
 * ====================================
 * Interactive map showing all towers in the Jardins Dona Isabel project
 * 
 * Features:
 * - Hover effects on different areas
 * - Image swapping on hover
 * - Overlay animations
 * - Accessibility support
 * 
 * @param {string} hostId - ID of the container element (default: 'mapHost')
 * @param {object} options - Configuration options
 */

class TowersMap {
  constructor(hostId = 'mapHost', options = {}) {
    this.host = document.getElementById(hostId);
    if (!this.host) {
      console.warn(`[TowersMap] Host element #${hostId} not found`);
      return;
    }

    this.isMobile = window.innerWidth <= 768;

    // Configuration
    this.config = {
      assetPath: options.assetPath || window.APP_CONFIG?.assetPath || '/',
      enableHover: options.enableHover !== false,
      debug: options.debug || false,
      ...options
    };

    const ap = this.config.assetPath;

    if (this.isMobile) {
      this.imagesByKey = {
        base: `${ap}site/img/home/towers/base-mobile.webp`,
        stay: `${ap}site/img/home/towers/stay-mobile.webp`,
        office: `${ap}site/img/home/towers/office-mobile.webp`,
        residence01: `${ap}site/img/home/towers/residences-mobile.webp`,
        residence02: `${ap}site/img/home/towers/residences-mobile.webp`,
        mall01: `${ap}site/img/home/towers/mall-mobile.webp`,
        mall02: `${ap}site/img/home/towers/mall-mobile.webp`,
        rooftop: `${ap}site/img/home/towers/rooftop-mobile.webp`,
        life: `${ap}site/img/home/towers/life-house-mobile.webp`
      };
      this.overlayByKey = {};
    } else {
      this.imagesByKey = {
        base: `${ap}site/img/home/towers/base.jpg`,
        stay: `${ap}site/img/home/towers/stay.jpg`,
        office: `${ap}site/img/home/towers/office.jpg`,
        residence01: `${ap}site/img/home/towers/residences.jpg`,
        residence02: `${ap}site/img/home/towers/residences.jpg`,
        mall01: `${ap}site/img/home/towers/mall.jpg`,
        mall02: `${ap}site/img/home/towers/mall.jpg`,
        rooftop: `${ap}site/img/home/towers/rooftop.jpg`,
        life: `${ap}site/img/home/towers/life-houses.jpg`
      };
      this.overlayByKey = {
        life: `${ap}site/img/home/towers/overlay-lifehouses.webp`,
        stay: `${ap}site/img/home/towers/overlay-stay.webp`,
        office: `${ap}site/img/home/towers/overlay-office.webp`,
        residence01: `${ap}site/img/home/towers/overlay-residences.webp`,
        residence02: `${ap}site/img/home/towers/overlay-residences.webp`,
        mall01: `${ap}site/img/home/towers/overlay-mall.webp`,
        mall02: `${ap}site/img/home/towers/overlay-mall.webp`,
        rooftop: `${ap}site/img/home/towers/overlay-rooftop.webp`
      };
    }

    // Mapping from nav keys to SVG data-keys
    this.navToSvgKeys = {
      'residence': ['residence01', 'residence02'],
      'life': ['life'],
      'stay': ['stay'],
      'mall': ['mall01', 'mall02'],
      'office': ['office'],
      'rooftop': ['rooftop']
    };

    // Reverse mapping: SVG key to nav key
    this.svgToNavKey = {
      'residence01': 'residence',
      'residence02': 'residence',
      'life': 'life',
      'stay': 'stay',
      'office': 'office',
      'mall01': 'mall',
      'mall02': 'mall',
      'rooftop': 'rooftop'
    };

    this.hoverIndicatorUrl = `${this.config.assetPath}site/img/home/hover-icon.webp`;

    // SVG dimensions
    this.svgWidth = this.isMobile ? 320 : 1872;
    this.svgHeight = this.isMobile ? 750 : 954;

    // State
    this.lastKey = null;
    this.mobileActiveKey = null;
    this.fadeHandles = new WeakMap();
    this.clipAnim = null;

    // Initialize
    this.init();
  }

  /**
   * Initialize the map
   */
  init() {
    this.host.classList.add('loading');
    this.injectSVG();
    this.host.classList.remove('loading');

    if (this.config.enableHover) {
      this.enableHover();
    }

    // Initialize navigation bar
    this.initNavBar();

    console.log('[TowersMap] Initialized successfully');
  }

  /**
   * Inject SVG markup into the host element
   */
  injectSVG() {
    const w = this.svgWidth;
    const h = this.svgHeight;
    const clipHalf = Math.round(h / 2);

    const interactiveAreas = this.isMobile
      ? `
        <g data-key="life" role="button" tabindex="0" aria-label="Life Houses">
          <rect x="15" y="330" fill="#fff" opacity="0" width="65" height="180"></rect>
        </g>
        <g data-key="stay" role="button" tabindex="0" aria-label="Stay">
          <rect x="70" y="280" fill="#fff" opacity="0" width="70" height="230"></rect>
        </g>
        <g data-key="office" role="button" tabindex="0" aria-label="Office">
          <rect x="130" y="240" fill="#fff" opacity="0" width="65" height="270"></rect>
        </g>
        <g data-key="residence01" role="button" tabindex="0" aria-label="Residence 01">
          <rect x="185" y="340" fill="#fff" opacity="0" width="50" height="170"></rect>
        </g>
        <g data-key="residence02" role="button" tabindex="0" aria-label="Residence 02">
          <rect x="225" y="300" fill="#fff" opacity="0" width="65" height="210"></rect>
        </g>
        <g data-key="mall01" role="button" tabindex="0" aria-label="Mall 01">
          <rect x="50" y="490" fill="#fff" opacity="0" width="130" height="45"></rect>
        </g>
        <g data-key="mall02" role="button" tabindex="0" aria-label="Mall 02">
          <rect x="185" y="490" fill="#fff" opacity="0" width="95" height="45"></rect>
        </g>`
      : `
        <g data-key="life" role="button" tabindex="0" aria-label="Life Houses">
          <rect x="427" y="369" fill="#fff" opacity="0" width="130" height="356"></rect>
        </g>
        <g data-key="stay" role="button" tabindex="0" aria-label="Stay">
          <rect x="564" y="295" fill="#fff" opacity="0" width="222" height="440"></rect>
        </g>
        <g data-key="office" role="button" tabindex="0" aria-label="Office">
          <rect x="846" y="312" fill="#fff" opacity="0" width="230" height="428"></rect>
        </g>
        <g data-key="residence01" role="button" tabindex="0" aria-label="Residence 01">
          <rect x="1090" y="442" fill="#fff" opacity="0" width="174" height="295"></rect>
        </g>
        <g data-key="residence02" role="button" tabindex="0" aria-label="Residence 02">
          <rect x="1292" y="384" fill="#fff" opacity="0" width="227" height="350"></rect>
        </g>
        <g data-key="mall01" role="button" tabindex="0" aria-label="Mall 01">
          <rect x="407" y="744" fill="#fff" opacity="0" width="721" height="72"></rect>
        </g>
        <g data-key="mall02" role="button" tabindex="0" aria-label="Mall 02">
          <rect x="1156" y="748" fill="#fff" opacity="0" width="396" height="100"></rect>
        </g>
        <g data-key="rooftop" role="button" tabindex="0" aria-label="Rooftop">
          <rect x="879" y="243" fill="#fff" opacity="0" width="163" height="64"></rect>
        </g>`;

    const overlayMarkup = this.isMobile
      ? ''
      : `<image id="overlayImage" class="overlay-img" x="0" y="0" width="${w}" height="${h}" 
               href="${this.config.assetPath}site/img/home/towers/residences-logo-line.webp" 
               xlink:href="${this.config.assetPath}site/img/home/towers/residences-logo-line.webp" 
               clip-path="url(#residenceOverlayClip)"></image>`;

    const svgHTML = `
      <svg id="mapSvg" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" 
           viewBox="0 0 ${w} ${h}" preserveAspectRatio="xMidYMid slice" role="img" 
           aria-label="Mapa interativo das torres" ${this.config.debug ? 'data-debug="true"' : ''}>
        <defs>
          <clipPath id="residenceOverlayClip" clipPathUnits="userSpaceOnUse">
            <rect id="overlayClipRect" x="0" y="${clipHalf}" width="${w}" height="${h}"></rect>
          </clipPath>
        </defs>
        
        <g id="bgLayer">
          <image id="bgA" class="fade-img is-visible" width="${w}" height="${h}" 
                 xlink:href="${this.imagesByKey.base}" href="${this.imagesByKey.base}"></image>
          <image id="bgB" class="fade-img" width="${w}" height="${h}" 
                 xlink:href="${this.imagesByKey.base}" href="${this.imagesByKey.base}"></image>
        </g>
        
        ${overlayMarkup}
        ${interactiveAreas}
      </svg>
    `;

    const doc = new DOMParser().parseFromString(svgHTML, 'text/html');
    const svg = doc.querySelector('#mapSvg');
    
    if (!svg) {
      console.error('[TowersMap] Failed to parse SVG');
      this.host.classList.add('error');
      return;
    }

    svg.removeAttribute('width');
    svg.removeAttribute('height');
    this.host.innerHTML = '';
    this.host.appendChild(svg);

    // Add hover indicator
    if (this.hoverIndicatorUrl) {
      const indicator = document.createElement('div');
      indicator.className = 'towers-map-hover-indicator';
      indicator.innerHTML = `<img src="${this.hoverIndicatorUrl}" alt="Passe o mouse para interagir" loading="lazy" />`;
      this.host.appendChild(indicator);
    }

    // Store references
    this.svg = svg;
    this.bgA = svg.querySelector('#bgA');
    this.bgB = svg.querySelector('#bgB');
    this.overlayImg = svg.querySelector('#overlayImage');
    this.overlayClipRect = svg.querySelector('#overlayClipRect');

    // Set initial state
    this.setInitialState();
  }

  /**
   * Set initial state for images
   */
  setInitialState() {
    if (this.bgA) this.bgA.style.opacity = '1';
    if (this.bgB) this.bgB.style.opacity = '0';
    if (this.overlayImg) {
      this.overlayImg.style.opacity = '0';
      this.overlayImg.style.pointerEvents = 'none';
    }
    if (this.overlayClipRect) {
      this.overlayClipRect.setAttribute('y', String(Math.round(this.svgHeight / 2)));
    }
  }

  /**
   * Enable hover/touch interactions
   */
  enableHover() {
    if (!this.svg) return;
    if (this.isMobile) return;

    this.host.classList.add('hover-unlocked');
    
    const interactiveAreas = this.svg.querySelectorAll('g[data-key]');
    interactiveAreas.forEach(area => {
      const key = area.getAttribute('data-key');

      if (this.isMobile) {
        area.addEventListener('touchstart', (e) => {
          e.preventDefault();
          this.handleMobileTap(key);
        }, { passive: false });
      } else {
        area.addEventListener('mouseenter', () => {
          this.showKey(key);
          const navKey = this.svgToNavKey[key];
          if (navKey) this.setActiveNavItem(navKey);
        });

        area.addEventListener('focus', () => {
          this.showKey(key);
          const navKey = this.svgToNavKey[key];
          if (navKey) this.setActiveNavItem(navKey);
        });

        area.addEventListener('mouseleave', () => {
          this.hideOverlay();
          this.clearActiveNavItems();
        });

        area.addEventListener('blur', () => {
          this.hideOverlay();
          this.clearActiveNavItems();
        });
      }
      
      area.addEventListener('click', () => {
        if (this.isMobile) {
          this.handleMobileTap(key);
          return;
        }
        const event = new CustomEvent('towerClick', { detail: { key } });
        this.host.dispatchEvent(event);
      });

      area.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          area.click();
        }
      });
    });

    console.log('[TowersMap] Hover effects enabled');
  }

  /**
   * Toggle highlight on mobile tap (tap same area again to deselect)
   */
  handleMobileTap(key) {
    if (this.mobileActiveKey === key) {
      this.mobileActiveKey = null;
      this.hideOverlay();
      this.clearActiveNavItems();
    } else {
      this.mobileActiveKey = key;
      this.showKey(key);
      const navKey = this.svgToNavKey[key];
      if (navKey) this.setActiveNavItem(navKey);
    }
  }

  /**
   * Show area on hover/tap
   */
  showKey(key) {
    const url = this.imagesByKey[key];
    
    if (url && this.bgB) {
      this.bgB.setAttribute('href', url);
      this.bgB.setAttribute('xlink:href', url);
      this.animateOpacity(this.bgB, 1, 400);
    }

    if (!this.isMobile && this.overlayImg) {
      const overlayUrl = this.overlayByKey[key];
      if (overlayUrl) {
        this.overlayImg.setAttribute('href', overlayUrl);
        this.overlayImg.setAttribute('xlink:href', overlayUrl);
      }
      this.animateOpacity(this.overlayImg, 1, 400);
    }

    if (!this.isMobile && this.overlayClipRect && this.lastKey !== key) {
      this.overlayClipRect.setAttribute('y', String(Math.round(this.svgHeight / 2)));
    }

    if (!this.isMobile) {
      this.animateClipTo(0, 1400);
    }

    this.lastKey = key;
  }

  /**
   * Hide overlay on mouse leave / tap deselect
   */
  hideOverlay() {
    if (this.bgA) this.bgA.classList.add('is-visible');
    this.animateOpacity(this.bgB, 0, 320);

    if (!this.isMobile) {
      this.animateOpacity(this.overlayImg, 0, 320);
      this.animateClipTo(Math.round(this.svgHeight / 2), 1200);
    }
  }

  /**
   * Initialize navigation bar interactions
   */
  initNavBar() {
    this.navItems = document.querySelectorAll('.towers-nav__item');

    if (!this.navItems.length) {
      console.warn('[TowersMap] Navigation bar not found');
      return;
    }

    this.navItems.forEach(item => {
      const navKey = item.getAttribute('data-nav-key');

      if (this.isMobile) {
        item.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleNavTap(navKey);
        });
      } else {
        item.addEventListener('mouseenter', () => this.handleNavHover(navKey));
        item.addEventListener('mouseleave', () => this.handleNavLeave());
      }

      item.setAttribute('tabindex', '0');
      item.setAttribute('role', 'button');

      if (!this.isMobile) {
        item.addEventListener('focus', () => this.handleNavHover(navKey));
        item.addEventListener('blur', () => this.handleNavLeave());
      }

      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (this.isMobile) {
            this.handleNavTap(navKey);
          } else {
            const svgKeys = this.navToSvgKeys[navKey];
            if (svgKeys && svgKeys.length > 0) {
              const event = new CustomEvent('towerClick', { detail: { key: svgKeys[0] } });
              this.host.dispatchEvent(event);
            }
          }
        }
      });
    });

    console.log('[TowersMap] Navigation bar initialized');
  }

  /**
   * Handle navigation bar item hover (desktop)
   */
  handleNavHover(navKey) {
    const svgKeys = this.navToSvgKeys[navKey];
    if (!svgKeys || svgKeys.length === 0) return;

    this.showKey(svgKeys[0]);
    this.setActiveNavItem(navKey);
  }

  /**
   * Handle navigation bar item leave (desktop)
   */
  handleNavLeave() {
    this.hideOverlay();
    this.clearActiveNavItems();
  }

  /**
   * Handle navigation bar item tap (mobile) - toggle on/off
   */
  handleNavTap(navKey) {
    const svgKeys = this.navToSvgKeys[navKey];
    if (!svgKeys || svgKeys.length === 0) return;

    if (this.mobileActiveKey === svgKeys[0]) {
      this.mobileActiveKey = null;
      this.hideOverlay();
      this.clearActiveNavItems();
    } else {
      this.mobileActiveKey = svgKeys[0];
      this.showKey(svgKeys[0]);
      this.setActiveNavItem(navKey);
    }
  }

  /**
   * Set active state on nav item
   */
  setActiveNavItem(navKey) {
    if (!this.navItems) return;
    this.navItems.forEach(item => {
      if (item.getAttribute('data-nav-key') === navKey) {
        item.classList.add('is-active');
      } else {
        item.classList.remove('is-active');
      }
    });
  }

  /**
   * Clear all active states from nav items
   */
  clearActiveNavItems() {
    if (!this.navItems) return;
    this.navItems.forEach(item => {
      item.classList.remove('is-active');
    });
  }

  /**
   * Animate opacity with easing
   */
  animateOpacity(el, to = 1, dur = 400) {
    if (!el) return;

    const from = parseFloat(getComputedStyle(el).opacity) || 0;
    
    if (this.fadeHandles.has(el)) {
      cancelAnimationFrame(this.fadeHandles.get(el));
    }

    const start = performance.now();
    const ease = t => 1 - Math.pow(1 - t, 3); // easeOutCubic

    const step = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const v = from + (to - from) * ease(p);
      el.style.opacity = String(v);
      
      if (p < 1) {
        this.fadeHandles.set(el, requestAnimationFrame(step));
      }
    };

    this.fadeHandles.set(el, requestAnimationFrame(step));
  }

  /**
   * Animate clip path
   */
  animateClipTo(toY, dur = 1400) {
    if (!this.overlayClipRect) return;

    if (this.clipAnim) cancelAnimationFrame(this.clipAnim);

    const fromY = parseFloat(this.overlayClipRect.getAttribute('y')) || 0;
    const start = performance.now();
    const ease = t => 1 - Math.pow(1 - t, 3); // easeOutCubic

    const step = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const y = fromY + (toY - fromY) * ease(p);
      this.overlayClipRect.setAttribute('y', y.toFixed(1));
      
      if (p < 1) {
        this.clipAnim = requestAnimationFrame(step);
      }
    };

    this.clipAnim = requestAnimationFrame(step);
  }

  /**
   * Destroy the map instance
   */
  destroy() {
    if (this.host) {
      this.host.innerHTML = '';
      this.host.classList.remove('loading', 'error', 'hover-unlocked');
    }
    // Clear nav item references
    this.navItems = null;
    console.log('[TowersMap] Destroyed');
  }
}

// Export for use in other modules
export default TowersMap;

// Auto-initialize if element exists on page load
document.addEventListener('DOMContentLoaded', () => {
  const mapHost = document.getElementById('mapHost');
  if (mapHost) {
    window.towersMapInstance = new TowersMap('mapHost', {
      enableHover: true,
      debug: false
    });
  }
});
